import React, { useState, useContext, useCallback, useRef } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';
import ColorPicker from '../ui/ColorPicker';
import VariableInserter from '../TemplateEditor/VariableInserter';
import TagInsertContext from '../../contexts/TagInsertContext';

// AI copy suggestions live on the user-emails backend; platform-emails has no /ai route.
const AI_API_BASE = process.env.REACT_APP_AI_API_URL
  || (process.env.NODE_ENV === 'production' ? 'https://user-emails.knkt.io/api/v1' : 'http://localhost:5014/api/v1');

const ContentBlockEditor = ({ block, onUpdate }) => {
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const textareaRef = useRef(null);

  // Get tag insert context (may be null if not wrapped in provider)
  const tagInsertContext = useContext(TagInsertContext);

  const handleChange = (field, value) => {
    onUpdate(block.id, {
      config: {
        ...block.config,
        [field]: value,
      },
    });
  };

  const handleStyleChange = (field, value) => {
    onUpdate(block.id, {
      styles: {
        ...block.styles,
        [field]: value,
      },
    });
  };

  const handleInsertVariable = (variable) => {
    const currentValue = block.config.body || '';
    handleChange('body', currentValue + variable);
  };

  // Handlers for TagInsertContext integration
  const handleTextareaFocus = useCallback((e) => {
    if (tagInsertContext?.registerField) {
      tagInsertContext.registerField(e.target, `content-body-${block.id}`);
    }
  }, [tagInsertContext, block.id]);

  const handleTextareaSelect = useCallback((e) => {
    if (tagInsertContext?.updateCursorPosition) {
      tagInsertContext.updateCursorPosition(e.target);
    }
  }, [tagInsertContext]);

  const handleTextareaKeyUp = useCallback((e) => {
    if (tagInsertContext?.updateCursorPosition) {
      tagInsertContext.updateCursorPosition(e.target);
    }
  }, [tagInsertContext]);

  const handleTextareaClick = useCallback((e) => {
    if (tagInsertContext?.updateCursorPosition) {
      tagInsertContext.updateCursorPosition(e.target);
    }
  }, [tagInsertContext]);

  // Handle programmatic value changes from tag insert context
  const handleTextareaInput = useCallback((e) => {
    // This catches value changes from the TagInsertContext
    const newValue = e.target.value;
    if (newValue !== block.config.body) {
      onUpdate(block.id, {
        config: {
          ...block.config,
          body: newValue,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.id, onUpdate]);

  const handleGenerateAI = async () => {
    setAiLoading(true);
    setShowSuggestions(false);

    try {
      const response = await fetch(`${AI_API_BASE}/ai/copy-suggestions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          blockType: 'content',
          currentContent: block.config.body || '',
          context: 'Music promotional email body copy',
          metadata: {
            // TODO: Get from template context if available
          },
        }),
      });

      const data = await response.json();

      if (data.success && data.suggestions) {
        setAiSuggestions(data.suggestions);
        setShowSuggestions(true);
      } else {
        alert('Failed to generate AI suggestions');
      }
    } catch (error) {
      console.error('AI suggestion error:', error);
      alert('Failed to generate AI suggestions: ' + error.message);
    } finally {
      setAiLoading(false);
    }
  };

  const handleSelectSuggestion = (suggestion) => {
    handleChange('body', suggestion);
    setShowSuggestions(false);
  };

  return (
    <div>
      <div className="editor-section">
        <h4 className="editor-section-title">Content</h4>

        <div className="editor-field">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <label style={{ margin: 0 }}>Body Text</label>
            <button
              type="button"
              onClick={handleGenerateAI}
              disabled={aiLoading}
              style={{
                background: 'linear-gradient(135deg, #A855F7 0%, #FF1493 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                padding: '6px 12px',
                fontSize: '13px',
                fontWeight: '500',
                cursor: aiLoading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                opacity: aiLoading ? 0.6 : 1,
              }}
            >
              {aiLoading ? (
                <>
                  <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles size={14} />
                  AI Suggestions
                </>
              )}
            </button>
          </div>

          <textarea
            ref={textareaRef}
            value={block.config.body || ''}
            onChange={(e) => handleChange('body', e.target.value)}
            onInput={handleTextareaInput}
            onFocus={handleTextareaFocus}
            onSelect={handleTextareaSelect}
            onClick={handleTextareaClick}
            onKeyUp={handleTextareaKeyUp}
            placeholder="Add your content here..."
            rows={10}
          />

          {showSuggestions && aiSuggestions.length > 0 && (
            <div style={{
              marginTop: '0.75rem',
              padding: '1rem',
              background: '#1a1a1a',
              border: '1px solid #333',
              borderRadius: '8px',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <h5 style={{ margin: 0, color: '#00D9FF', fontSize: '14px', fontWeight: '600' }}>
                  ✨ AI Suggestions
                </h5>
                <button
                  type="button"
                  onClick={() => setShowSuggestions(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#999',
                    cursor: 'pointer',
                    fontSize: '18px',
                    padding: '0',
                  }}
                >
                  ×
                </button>
              </div>
              {aiSuggestions.map((suggestion, index) => (
                <div
                  key={index}
                  onClick={() => handleSelectSuggestion(suggestion)}
                  style={{
                    padding: '0.75rem',
                    background: '#0f0f0f',
                    border: '1px solid #2a2a2a',
                    borderRadius: '6px',
                    marginBottom: index < aiSuggestions.length - 1 ? '0.5rem' : 0,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    color: '#ccc',
                    fontSize: '13px',
                    lineHeight: '1.6',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#141414';
                    e.currentTarget.style.borderColor = '#00D9FF';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#0f0f0f';
                    e.currentTarget.style.borderColor = '#2a2a2a';
                  }}
                >
                  {suggestion}
                </div>
              ))}
            </div>
          )}

          <VariableInserter
            onInsert={handleInsertVariable}
            targetField="body"
          />
        </div>
      </div>

      <div className="editor-section">
        <h4 className="editor-section-title">Styling</h4>

        <ColorPicker
          label="Text Color"
          color={block.styles.color || '#ffffff'}
          onChange={(color) => handleStyleChange('color', color)}
        />

        <ColorPicker
          label="Background Color"
          color={block.styles.backgroundColor || '#000000'}
          onChange={(color) => handleStyleChange('backgroundColor', color)}
        />

        <div className="editor-field" style={{ marginTop: '1rem' }}>
          <label>Text Alignment</label>
          <select
            value={block.styles.textAlign || 'left'}
            onChange={(e) => handleStyleChange('textAlign', e.target.value)}
          >
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </select>
        </div>

        <div className="editor-field">
          <label>Font Size (px)</label>
          <input
            type="number"
            value={block.styles.fontSize || 16}
            onChange={(e) => handleStyleChange('fontSize', parseInt(e.target.value))}
            min="8"
            max="72"
          />
        </div>
      </div>
    </div>
  );
};

export default ContentBlockEditor;
